import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import dayjs from 'dayjs';
import { GraphData } from 'components/charts/types';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import styles from './style.module.scss';
import abbreviateNumber from 'utils/abbreviate-number';
import { buildClassName } from 'utils/build-class-name';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import Image from 'next/image';

import type { InsightsResponse, MonthlyMedian } from '@zoocasa/go-search';

dayjs.extend(LocalizedFormat);

type ChartDataPoint = {
  x: string;
  y: number;
  medianPriceOneYearAgo: number;
};

/**
  * Transforms InsightsResponse data to match the mockResponse structure
  * @param insightsResponse - The insights response from the API
  * @returns Data in the format expected by formatSoldPriceChartData
  */
export const transformInsightsToChartData = (insightsResponse: InsightsResponse | undefined) => {
  const medianPriceTrends = insightsResponse?.medianPriceTrends;

  if (!medianPriceTrends || medianPriceTrends.length === 0) {
    return { data: []};
  }

  // Step 1: Build price lookup by month
  const priceByMonth: Record<string, number> = {};
  medianPriceTrends.forEach((trend: MonthlyMedian) => {
    const month = trend.date;
    const median = trend.medianPrice;
    priceByMonth[month] = median;
  });

  // Step 2: Helper to subtract 12 months
  const subtract12Months = (monthStr: string) => {
    const [year, month] = monthStr.split('-').map(Number);
    const date = new Date(year, month - 1);
    date.setFullYear(date.getFullYear() - 1);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  };

  // Step 3: Pair current and 12-month-ago prices
  const allResults = Object.keys(priceByMonth)
    .sort()
    .map(currentMonth => {
      const prevMonth = subtract12Months(currentMonth);
      const currentPrice = priceByMonth[currentMonth];
      const pastPrice = priceByMonth[prevMonth];

      if (pastPrice !== undefined) {
        return {
          soldAt: currentMonth,
          medianPrice: currentPrice,
          medianPriceOneYearAgo: pastPrice,
        };
      }
      return null;
    })
    .filter(row => row !== null);

  // Step 4: Get only the most recent 12 months
  const sortedResults = allResults.sort((a, b) => a.soldAt.localeCompare(b.soldAt));
  const latest12Months = sortedResults.slice(-12);

  return { data: latest12Months };
};

export const formatSoldPriceChartData = (response: { data: Array<{ soldAt: string; medianPrice: number; medianPriceOneYearAgo: number }> }): { formattedChartData: ChartDataPoint[]; minValue: number; maxValue: number } => {
  const formattedChartData: ChartDataPoint[] = response.data.map(r => ({
    x: r.soldAt,
    y: r.medianPrice,
    medianPriceOneYearAgo: r.medianPriceOneYearAgo,
  })).filter((data): data is ChartDataPoint => Boolean(data.x && data.y));

  const medianPrices = formattedChartData.map(d => d.y).filter(price => price !== null);
  const minValue = Math.floor(Math.min(...medianPrices) / 50000) * 50000;
  const maxValue = Math.ceil(Math.max(...medianPrices) / 50000) * 50000;

  return { formattedChartData, minValue, maxValue };
};

type TooltipElementProps = {
  x: number;
  y: number;
  visible: boolean;
  date: string | null;
  price: string | null;
  dateOneYearAgo: string | null;
  priceOneYearAgo: string | null;
  hasValidPercentageData: boolean;
  percentageChange: number | null;
};

const TooltipElement = ({ x, y, visible, date, price, dateOneYearAgo, priceOneYearAgo, hasValidPercentageData, percentageChange }: TooltipElementProps) => {
  return (
    <div id="tooltip" style={{ left: x, top: y }} className={buildClassName(styles['tooltip-content'], visible && styles.visible)}>
      <span className={styles.date}>{date}</span>
      <span className={styles.price}>{price}</span>
      <div className={styles.stats}>
        <div className={styles.row}>
          <span className={styles['one-year-ago']}>{dateOneYearAgo}</span>
          <span className={styles['one-year-ago-price']}>{priceOneYearAgo}</span>
        </div>
        {hasValidPercentageData &&
          <div className={styles.row}>
            <span className={styles['current-median']}>Current Median <br/>V.S. 12 Months Ago</span>
            <span className={buildClassName(styles['current-median-percentage'], percentageChange > 0 ? styles.positive : styles.negative)}>
              <Image
                src={percentageChange > 0 ? '/next/assets/images/trend-up.svg' : '/next/assets/images/trend-down.svg'}
                alt='Trend line'
                width={24}
                height={24}
              />
              {percentageChange > 0 ? '+' : ''}{percentageChange?.toFixed(2)}%
            </span>
          </div>
        }
      </div>
    </div>
  );
};

export default function InsightsChart({ insightsData, hideTitle }: { insightsData: InsightsResponse; hideTitle?: boolean }) {
  const { formatCurrency } = useI18nFormatting();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [currentTooltipDate, setCurrentTooltipDate] = useState(null);
  const [currentTooltipDateOneYearAgo, setCurrentTooltipDateOneYearAgo] = useState(null);
  const [currentTooltipPrice, setCurrentTooltipPrice] = useState(null);
  const [currentTooltipPriceOneYearAgo, setCurrentTooltipPriceOneYearAgo] = useState(null);
  const [currentTooltipPercentageChange, setCurrentTooltipPercentageChange] = useState(null);
  const [currentTooltipX, setCurrentTooltipX] = useState(0);
  const [currentTooltipY, setCurrentTooltipY] = useState(0);
  const [hasValidPercentageData, setHasValidPercentageData] = useState(false);

  const NO_DATA_TEXT = 'No data';
  const DESIRED_Y_AXIS_TICKS = 8;

  const insightsResponse = transformInsightsToChartData(insightsData);
  const { formattedChartData } = formatSoldPriceChartData(insightsResponse);
  const hasData = formattedChartData && formattedChartData.length > 0;

  const percentageChange = (previous: number, current: number) => {
    // Return null if previous value is 0 to avoid infinity
    if (previous === 0) {
      return null;
    }
    return ((current - previous) / previous) * 100;
  };

  const drawChart = () => {
    // Set up dimensions and margins — use container width so chart fills its parent
    const margin = { top: 20, right: 50, bottom: 30, left: 50 };
    const containerWidth = containerRef.current?.clientWidth || 800;
    const width = containerWidth - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;

    const svg = d3.select(svgRef.current)
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create scales
    const x = d3.scaleTime()
      .domain(d3.extent(formattedChartData, d => new Date(d.x)) as [Date, Date])
      .range([0, width]);

    // Use exact months from data for x-axis ticks
    const months = formattedChartData.map(d => new Date(d.x));
    const xAxis = d3.axisBottom(x)
      .tickValues(months)
      .tickFormat((t: Date | d3.NumberValue) => dayjs(t as Date).utc().format('MMM \'YY'))
      .tickSize(0);

    const y = d3.scaleLinear()
      .domain([d3.min(formattedChartData, d => d.y), d3.max(formattedChartData, d => d.y)])
      .range([height, 0]);

    // Add central vertical grey line
    const centerX = width / 2;
    svg.append('line')
      .attr('x1', centerX)
      .attr('x2', centerX)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#e8e8e8')
      .attr('stroke-width', 1);

    // Create line generator with smooth curve
    const line = d3.line<GraphData>()
      .x(d => x(new Date(d.x)))
      .y(d => y(d.y))
      .curve(d3.curveMonotoneX);

    // Add X axis
    svg.append('g')
      .attr('transform', `translate(21,${height + 10})`)
      .call(xAxis)
      .attr('class', styles['x-axis']);

    d3.selectAll('.domain')
      .style('stroke', 'none');

    // Calculate dynamic tick interval to achieve desired number of ticks
    const actualMin = d3.min(formattedChartData, d => d.y);
    const actualMax = d3.max(formattedChartData, d => d.y);
    const range = actualMax - actualMin;
    const tickInterval = Math.ceil(range / (DESIRED_Y_AXIS_TICKS - 1));
    const tickValues = d3.range(actualMin, actualMax + tickInterval, tickInterval);

    // Add Y axis
    const yAxis = d3.axisLeft(y)
      .tickValues(tickValues)
      .tickFormat((t: d3.NumberValue) => `$${abbreviateNumber(t.valueOf())}`)
      .tickSize(0);

    svg.append('g')
      .call(yAxis)
      .attr('class', styles['y-axis']);

    d3.selectAll('.domain')
      .style('stroke', 'none');

    // Add horizontal grid lines
    svg.append('g')
      .attr('class', 'grid')
      .call(yAxis
        .tickSize(-width)
        .tickFormat(() => ''))
      .style('color', '#e8e8e8');

    // Add the line path
    svg.append('path')
      .datum(formattedChartData)
      .attr('fill', 'none')
      .attr('class', styles['line-path'])
      .attr('d', line);

    // Add invisible dots for hover detection
    svg.selectAll('circle')
      .data(formattedChartData)
      .enter()
      .append('circle')
      .attr('cx', (d: { x: string | number | Date }) => x(new Date(d.x)))
      .attr('cy', (d: { y: number }) => y(d.y))
      .attr('r', 5)
      .style('fill', 'transparent');

    // Create a group for the hover effect
    const hoverGroup = svg.append('g')
      .attr('class', 'hover-group')
      .style('display', 'none');

    // Add hover circle
    const hoverCircle = hoverGroup.append('circle')
      .attr('r', 5)
      .attr('class', styles['hover-circle']);

    // Add hover line
    const hoverLine = hoverGroup.append('line')
      .attr('class', styles['hover-line']);

    // Function to find closest point
    const bisect = d3.bisector((d: GraphData) => new Date(d.x).getTime()).left;

    // Add hover effects
    svg.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'transparent');

    const tooltip = d3.select('#tooltip');

    svg.on('mouseover', () => {
      setTooltipVisible(true);
      hoverGroup.style('display', null);
    })
      .on('mouseout', () => {
        setTooltipVisible(false);
        hoverGroup.style('display', 'none');
      })
      .on('mousemove', function(event) {
        const [xPos] = d3.pointer(event);
        const x0 = x.invert(xPos);
        const i = bisect(formattedChartData, x0, 1);
        let d0 = formattedChartData[i - 1];
        let d1 = formattedChartData[i];
        // Clamp to valid range to prevent undefined errors
        if (!d0) d0 = d1;
        if (!d1) d1 = d0;
        const d = x0.getTime() - new Date(d0.x).getTime() > new Date(d1.x).getTime() - x0.getTime() ? d1 : d0;

        // Update hover circle position
        hoverCircle
          .attr('cx', x(new Date(d.x)))
          .attr('cy', y(d.y));

        // Update hover line
        hoverLine
          .attr('x1', x(new Date(d.x)))
          .attr('x2', x(new Date(d.x)))
          .attr('y1', 0)
          .attr('y2', height);

        // Update tooltip
        tooltip
          .style('visibility', 'visible');

        setCurrentTooltipDate(dayjs(d.x).format('MMMM YYYY'));
        setCurrentTooltipPrice(formatCurrency(d.y));
        setCurrentTooltipDateOneYearAgo(dayjs(d.x).subtract(1, 'year').format('MMMM YYYY'));

        // Check if medianPriceOneYearAgo is 0 and handle accordingly
        const medianPriceOneYearAgo = formattedChartData[i - 1].medianPriceOneYearAgo;
        if (medianPriceOneYearAgo === 0) {
          setCurrentTooltipPriceOneYearAgo(NO_DATA_TEXT);
          setHasValidPercentageData(false);
        } else {
          setCurrentTooltipPriceOneYearAgo(formatCurrency(medianPriceOneYearAgo));
          const percentage = percentageChange(medianPriceOneYearAgo, d.y);
          setCurrentTooltipPercentageChange(percentage);
          setHasValidPercentageData(percentage !== null);
        }

        // Position relative to containerRef (the tooltip's positioning context, see
        // style.module.scss), not the raw viewport — clientX/clientY alone ignore how far
        // the page has scrolled, which is what pushed the tooltip up near the page top.
        const containerRect = containerRef.current?.getBoundingClientRect();
        const localX = event.clientX - (containerRect?.left ?? 0);
        const localY = event.clientY - (containerRect?.top ?? 0);
        if (event.clientX < 500) {
          setCurrentTooltipX(localX - 50);
        } else {
          setCurrentTooltipX(localX - window.innerWidth / 5);
        }
        setCurrentTooltipY(localY - 50);
      });
  };

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;
    if (hasData) {
      d3.select(svgRef.current).selectAll('*').remove();
      drawChart();
    }

    const handleResize = () => {
      if (!svgRef.current || !containerRef.current) return;
      if (hasData) {
        d3.select(svgRef.current).selectAll('*').remove();
        drawChart();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div className={styles.component} ref={containerRef}>
      {!hideTitle && (
        <h2 className={styles.title}>12-Month Price Change  <span className={styles.subtitle}>Monthly Median Home Price</span></h2>
      )}
      <div className={styles.chartContainer}>
        {hasData ? (
          <>
            <TooltipElement
              x={currentTooltipX}
              y={currentTooltipY}
              visible={tooltipVisible}
              date={currentTooltipDate}
              price={currentTooltipPrice}
              dateOneYearAgo={currentTooltipDateOneYearAgo}
              priceOneYearAgo={currentTooltipPriceOneYearAgo}
              hasValidPercentageData={hasValidPercentageData}
              percentageChange={currentTooltipPercentageChange}
            />
            <svg ref={svgRef}></svg>
          </>
        ) : (
          <div className={styles['no-data']}>
            <Image src='/assets/images/market-insights-no-data.png' alt='No data' width={300} height={250} />
            <div className={styles['no-data-disclaimer']}>Price trend not available</div>
          </div>
        )}
      </div>
      <div className={styles['last-updated']}>
        Last Updated: {dayjs().format('lll')}
      </div>
    </div>
  );
}
